import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { Smartphone, Gamepad2 } from "lucide-react";

const VALID_PLATFORMS = ["mobile", "console"];

export default function Leagues() {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const rawPlatform = params.get("platform");
  const platform = VALID_PLATFORMS.includes(rawPlatform) ? rawPlatform : "mobile";
  const setPlatform = (p) => setParams({ platform: p }, { replace: true });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leagues");
        setLeagues(data);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Split by platform; treat anything that's not explicitly console/byol as mobile.
  const { mobileLeagues, consoleLeagues } = useMemo(() => {
    const mob = [];
    const con = [];
    for (const lg of leagues) {
      if (lg.league_type === "byol") continue; // BYOL has its own admin tab
      if (lg.platform === "console") con.push(lg);
      else mob.push(lg);
    }
    return { mobileLeagues: mob, consoleLeagues: con };
  }, [leagues]);

  const activeLeagues = platform === "console" ? consoleLeagues : mobileLeagues;

  return (
    <div data-testid="leagues-page">
      <SectionTitle kicker="// archive" testId="leagues-title">All Seasons</SectionTitle>

      {/* Platform split — Mobile / Console separate menus */}
      <div
        className="flex gap-1 border-b border-[#E4E4E7] dark:border-[#2A2A30] mb-6 flex-wrap"
        data-testid="leagues-platform-tabs"
        role="tablist"
      >
        <PlatformTab
          id="mobile"
          label="Mobile"
          count={mobileLeagues.length}
          Icon={Smartphone}
          active={platform === "mobile"}
          onClick={() => setPlatform("mobile")}
          testId="leagues-tab-mobile"
        />
        <PlatformTab
          id="console"
          label="Console"
          count={consoleLeagues.length}
          Icon={Gamepad2}
          active={platform === "console"}
          onClick={() => setPlatform("console")}
          testId="leagues-tab-console"
        />
      </div>

      {loading ? (
        <TactLoader />
      ) : activeLeagues.length === 0 ? (
        <Panel
          className="font-mono text-[#71717A] text-sm"
          testId={`leagues-empty-${platform}`}
        >
          [ NO {platform.toUpperCase()} SEASONS YET ]
        </Panel>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid={`leagues-list-${platform}`}
        >
          {activeLeagues.map((lg) => (
            <LeagueCard key={lg.id} lg={lg} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlatformTab({ id, label, count, Icon, active, onClick, testId }) {
  // Electric-green accent so the platform switcher matches the new FUT palette.
  const base =
    "inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors";
  const cls = active
    ? "border-[#00FF87] text-[#00FF87]"
    : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]";
  return (
    <button
      role="tab"
      aria-selected={active}
      aria-controls={`leagues-panel-${id}`}
      data-testid={testId}
      onClick={onClick}
      className={`${base} ${cls}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      <span
        className={`font-mono text-[9px] border px-1.5 py-0.5 ${
          active
            ? "border-[#00FF87]/50 text-[#00FF87]"
            : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A]"
        }`}
        data-testid={`${testId}-count`}
      >
        {count}
      </span>
    </button>
  );
}

function LeagueCard({ lg }) {
  const pct = Math.min(100, Math.round((lg.player_count / lg.capacity) * 100));
  const color =
    lg.status === "open"
      ? "text-[#7C3AED]"
      : lg.status === "locked"
      ? "text-[#00FF87]"
      : "text-[#71717A]";
  return (
    <Link
      to={`/leagues/${lg.id}`}
      className="block bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-6 hover:border-[#7C3AED] transition-colors"
      data-testid={`league-${lg.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-heading text-2xl uppercase">{lg.name}</span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.3em] ${color}`}>
          {lg.status}
        </span>
      </div>
      <div className="font-mono text-sm text-[#09090B] dark:text-[#F4F4F5]">
        {lg.player_count} / {lg.capacity} players
      </div>
      <div className="mt-3 h-1.5 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30]">
        <div
          className="h-full bg-gradient-to-r from-[#7C3AED] to-[#00FF87]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </Link>
  );
}
